import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import CheckCircle from '@mui/icons-material/CheckCircle'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  deleteMPDataWithoutParams,
  getMPDataWithoutParams,
  getMPDataWithParams,
  postMPDataWithoutParams,
} from 'src/mentor/services'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import SignaturePad from 'src/consumer/components/Helpers/SignaturePad/SignaturePad'

const STAGE_2_DETAIL_PATH = '/api/secure/practitioner-onboarding/stage2/detail'
const STAGE_2_SAVE_PATH = '/api/secure/practitioner-onboarding/stage2/save'
const STAGE_2_DRAFT_PATH = '/api/secure/practitioner-onboarding/stage2/draft'
const DRAFT_AUTOSAVE_DEBOUNCE_MS = 1500

const LANGUAGE_OPTIONS = [
  'English',
  'Hindi',
  'Tamil',
  'Telugu',
  'Bengali',
  'Marathi',
  'Gujarati',
  'Kannada',
  'Malayalam',
  'Punjabi',
  'Urdu',
]

const MONTH_OPTIONS = [
  '01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12',
]

/**
 * Phase 14e — polished Stage 2 page that the applicant fills after support has approved Stage 1.
 *
 * Three modes driven by `applicationStage`:
 *   - stage_1_approved | stage_2_in_progress: edit mode
 *   - stage_2_completed:                        already-submitted mode (read-only summary)
 *   - rejected:                                 error message routing them to support contact
 */
export default function Stage2OnboardingPage() {
  const navigate = useNavigate()
  const { practitionerId: routePractitionerId } = useParams()
  const [state, setState] = useState({ loading: true, error: '', detail: null })
  const [form, setForm] = useState({
    profilePicId: '',
    longDesc: '',
    languages: [],
    experienceStartMonth: '',
    experienceStartYear: '',
    mandatoryCredentialDocFileId: '',
    mandatoryCredentialDocTitle: '',
    signatureMediaId: '',
  })
  const [saving, setSaving] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  // Sign-and-submit dialog state. Opens when the practitioner clicks Submit without having
  // already provided a signature, so the canvas is the last interaction before the server save.
  const [signDialogOpen, setSignDialogOpen] = useState(false)
  // Phase 14g — auto-save draft on the server (collection practitioner_onboarding_drafts).
  // pendingDraft holds the draft payload returned on first load IF it differs meaningfully
  // from the server-hydrated form; surfaced via a Resume / Discard alert banner.
  const [pendingDraft, setPendingDraft] = useState(null)
  // Suppresses the next auto-save tick on hydration paths (server detail load, draft restore,
  // post-save reload) so the just-loaded data isn't immediately re-saved as a draft.
  const draftAutosaveSuppressedRef = useRef(true)
  const [lastDraftSavedAt, setLastDraftSavedAt] = useState(null)

  async function loadDetail() {
    setState((prev) => ({ ...prev, loading: true, error: '' }))
    const params = routePractitionerId ? { 'practitioner-id': routePractitionerId } : {}
    const resp = await getMPDataWithParams(STAGE_2_DETAIL_PATH, params)
    if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
      const data = getDataMapFromCommonResponse(resp)?.stage2 || null
      if (data) {
        // Suppress the next auto-save tick — this setForm is hydration, not user edit.
        draftAutosaveSuppressedRef.current = true
        setForm({
          profilePicId: data.profilePicId || '',
          longDesc: data.longDesc || '',
          languages: data.languages || [],
          experienceStartMonth: data.experienceStartMonth || '',
          experienceStartYear: data.experienceStartYear || '',
          mandatoryCredentialDocFileId: data.mandatoryCredentialDocFileId || '',
          mandatoryCredentialDocTitle: data.mandatoryCredentialDocTitle || '',
          signatureMediaId: data.signatureMediaId || '',
        })
      }
      setState({ loading: false, error: '', detail: data })

      // Phase 14g — check for a server-side draft after the detail loads. If it differs from
      // the freshly-hydrated form, surface the Resume / Discard banner.
      try {
        const draftResp = await getMPDataWithoutParams(STAGE_2_DRAFT_PATH)
        if (draftResp?.status === 200 && isCommonResponseSuccessfulV2(draftResp)) {
          const draft = getDataMapFromCommonResponse(draftResp)?.draft
          if (draft?.payload) {
            const interestingFromDraft = JSON.stringify({
              longDesc: draft.payload.longDesc,
              languages: draft.payload.languages,
              experienceStartMonth: draft.payload.experienceStartMonth,
              experienceStartYear: draft.payload.experienceStartYear,
              mandatoryCredentialDocFileId: draft.payload.mandatoryCredentialDocFileId,
            })
            const interestingFromForm = JSON.stringify({
              longDesc: data?.longDesc || '',
              languages: data?.languages || [],
              experienceStartMonth: data?.experienceStartMonth || '',
              experienceStartYear: data?.experienceStartYear || '',
              mandatoryCredentialDocFileId: data?.mandatoryCredentialDocFileId || '',
            })
            if (interestingFromDraft !== interestingFromForm) {
              setPendingDraft({
                payload: draft.payload,
                savedAt: draft.modifiedTime ? Date.parse(draft.modifiedTime) : null,
              })
            }
          }
        }
      } catch {
        // Draft fetch is best-effort.
      }
    } else {
      setState({
        loading: false,
        error: resp?.data?.message || 'Could not load your Stage 2 form.',
        detail: null,
      })
    }
  }

  useEffect(() => {
    loadDetail()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routePractitionerId])

  // Auto-save the in-flight form to the server-side draft (Phase 14g). Debounced 1.5s; skipped
  // on the first render after a hydration so the just-loaded data isn't immediately re-saved.
  useEffect(() => {
    if (draftAutosaveSuppressedRef.current) {
      draftAutosaveSuppressedRef.current = false
      return
    }
    const timer = setTimeout(async () => {
      try {
        await postMPDataWithoutParams(STAGE_2_DRAFT_PATH, form)
        setLastDraftSavedAt(Date.now())
      } catch {
        // Best-effort — next change retries.
      }
    }, DRAFT_AUTOSAVE_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [form])

  function handleDraftRestore() {
    if (!pendingDraft?.payload) return
    draftAutosaveSuppressedRef.current = true
    setForm((prev) => ({ ...prev, ...pendingDraft.payload }))
    setPendingDraft(null)
    toast.success('Draft restored. Review the fields and Submit when ready.')
  }

  async function handleDraftDiscard() {
    try {
      await deleteMPDataWithoutParams(STAGE_2_DRAFT_PATH)
    } catch {
      // best-effort
    }
    setPendingDraft(null)
    setLastDraftSavedAt(null)
  }

  // Progress indicator — drives the LinearProgress bar at the top.
  const completionPct = useMemo(() => {
    const checks = [
      Boolean(form.longDesc.trim()),
      form.languages.length > 0,
      Boolean(form.experienceStartYear),
      Boolean(form.mandatoryCredentialDocFileId),
      Boolean(form.profilePicId),
    ]
    const passed = checks.filter(Boolean).length
    return Math.round((passed / checks.length) * 100)
  }, [form])

  async function persist(submitFlag) {
    const setBusy = submitFlag ? setSubmitting : setSaving
    setBusy(true)
    const payload = {
      practitionerId: routePractitionerId || state.detail?.practitionerId || undefined,
      profilePicId: form.profilePicId.trim() || undefined,
      longDesc: form.longDesc.trim() || undefined,
      languages: form.languages,
      experienceStartMonth: form.experienceStartMonth || undefined,
      experienceStartYear: form.experienceStartYear || undefined,
      mandatoryCredentialDocFileId: form.mandatoryCredentialDocFileId.trim() || undefined,
      mandatoryCredentialDocTitle: form.mandatoryCredentialDocTitle.trim() || undefined,
      signatureMediaId: form.signatureMediaId.trim() || undefined,
      submit: Boolean(submitFlag),
    }
    const resp = await postMPDataWithoutParams(STAGE_2_SAVE_PATH, payload)
    setBusy(false)
    if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
      const data = getDataMapFromCommonResponse(resp)?.stage2 || null
      setState((prev) => ({ ...prev, detail: data }))
      toast.success(submitFlag ? 'Submitted for review.' : 'Saved your progress.')
      if (submitFlag) {
        // Re-load to lock the page into the completed-state UI.
        loadDetail()
      }
    } else {
      toast.error(resp?.data?.message || 'Could not save Stage 2 form.')
    }
  }

  if (state.loading) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (state.error) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Alert severity="error">{state.error}</Alert>
      </Container>
    )
  }

  const detail = state.detail || {}
  const stage = detail.applicationStage
  const kycRejected =
    stage === 'rejected' || detail.kycStatus === 'rejected'
  const alreadyCompleted = stage === 'stage_2_completed'

  if (kycRejected) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Paper sx={{ p: 4, borderRadius: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            Application closed
          </Typography>
          <Alert severity="warning">
            Your application is no longer active. Please reach out to the team if you believe
            this is in error or you'd like to re-apply with updated details.
          </Alert>
        </Paper>
      </Container>
    )
  }

  if (alreadyCompleted) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Paper sx={{ p: 4, borderRadius: 3 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
            <CheckCircle color="success" />
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Stage 2 submitted
            </Typography>
          </Stack>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            Thanks, {detail.name || 'there'}! Our team has been notified and will reach out shortly
            with next steps. You'll receive a confirmation on email and WhatsApp.
          </Typography>
          <Button variant="outlined" onClick={() => navigate('/mentor-home/profile')}>
            Go to your profile
          </Button>
        </Paper>
      </Container>
    )
  }

  return (
    <Container maxWidth="md" sx={{ py: { xs: 2, md: 4 } }}>
      <Paper sx={{ p: { xs: 2, md: 4 }, borderRadius: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
          <Avatar sx={{ width: 56, height: 56 }}>
            {(detail.name || '?').charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Welcome, {detail.name || 'there'} — finish your profile
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Stage 2 of 2 · About 10 minutes · Saved automatically as you go.
            </Typography>
          </Box>
          <Chip label={`${completionPct}% complete`} color={completionPct === 100 ? 'success' : 'default'} />
        </Stack>
        <LinearProgress variant="determinate" value={completionPct} sx={{ borderRadius: 1, mb: 3 }} />

        {stage === 'stage_2_in_progress' && (
          <Alert severity="info" sx={{ mb: 2 }}>
            You&apos;ve started Stage 2. Pick up where you left off — your previous answers are below.
          </Alert>
        )}

        {/* Phase 14g — server-side draft restore banner. Surfaces when an in-flight draft was
            saved on a different device / session and meaningfully differs from what we just
            loaded from the server. */}
        {pendingDraft && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            action={
              <Stack direction="row" spacing={1}>
                <Button size="small" color="inherit" onClick={handleDraftDiscard}>
                  Discard
                </Button>
                <Button size="small" variant="contained" onClick={handleDraftRestore}>
                  Resume draft
                </Button>
              </Stack>
            }
          >
            You have unsaved Stage 2 changes
            {pendingDraft.savedAt
              ? ` from ${new Date(pendingDraft.savedAt).toLocaleString(undefined, {
                  day: 'numeric',
                  month: 'short',
                  hour: '2-digit',
                  minute: '2-digit',
                })}`
              : ''}
            . Resume to pick up where you left off.
          </Alert>
        )}

        {lastDraftSavedAt && !pendingDraft && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Draft saved · {new Date(lastDraftSavedAt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
          </Typography>
        )}

        <Stack spacing={2.5}>
          <Section title="A photo of you" subtitle="Helps us recognise you and looks great on your public profile.">
            <TextField
              label="Profile photo media id"
              placeholder="Upload via the Profile page first, then paste the id here."
              value={form.profilePicId}
              onChange={(e) => setForm((p) => ({ ...p, profilePicId: e.target.value }))}
              fullWidth
              helperText="Temporary: use the Profile uploader to host the image; this field will accept the upload widget directly soon."
            />
          </Section>

          <Section title="About you" subtitle="A longer description for your public profile.">
            <TextField
              label="Long bio"
              multiline
              minRows={4}
              value={form.longDesc}
              onChange={(e) => setForm((p) => ({ ...p, longDesc: e.target.value }))}
              fullWidth
              helperText="Aim for 4–6 lines on your training, approach, and what makes your practice yours."
            />
            {detail.shortDesc && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Stage 1 short bio: {detail.shortDesc}
              </Typography>
            )}
          </Section>

          <Section title="Languages you practise in">
            <Autocomplete
              multiple
              freeSolo
              options={LANGUAGE_OPTIONS}
              value={form.languages}
              onChange={(_e, val) => setForm((p) => ({ ...p, languages: val }))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Languages"
                  helperText="Pick from the list or type your own and press Enter."
                />
              )}
            />
          </Section>

          <Section title="Experience" subtitle="When did you start practising?">
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Autocomplete
                options={MONTH_OPTIONS}
                value={form.experienceStartMonth || null}
                onChange={(_e, val) =>
                  setForm((p) => ({ ...p, experienceStartMonth: val || '' }))
                }
                sx={{ minWidth: 140 }}
                renderInput={(params) => <TextField {...params} label="Month (optional)" />}
              />
              <TextField
                label="Year"
                placeholder="e.g. 2018"
                value={form.experienceStartYear}
                onChange={(e) =>
                  setForm((p) => ({ ...p, experienceStartYear: e.target.value.replace(/\D/g, '').slice(0, 4) }))
                }
                inputProps={{ inputMode: 'numeric', maxLength: 4 }}
                fullWidth
              />
            </Stack>
          </Section>

          <Section
            title="Credential document"
            subtitle="Required — your registration / license certificate (RCI, MCI, IDA, or equivalent)."
          >
            <TextField
              label="Document title"
              placeholder="e.g. RCI Certificate 2021"
              value={form.mandatoryCredentialDocTitle}
              onChange={(e) =>
                setForm((p) => ({ ...p, mandatoryCredentialDocTitle: e.target.value }))
              }
              fullWidth
              sx={{ mb: 1 }}
            />
            <TextField
              label="Document file id"
              placeholder="Upload from your phone / desktop and paste the file id here."
              value={form.mandatoryCredentialDocFileId}
              onChange={(e) =>
                setForm((p) => ({ ...p, mandatoryCredentialDocFileId: e.target.value }))
              }
              fullWidth
              helperText="Temporary: paste the upload id from the existing media uploader; the inline uploader for Stage 2 lands in a follow-up."
            />
          </Section>

          <Section
            title="Signature"
            subtitle="Sign on the canvas with your finger / mouse / pen, or upload a clean image. Required before you can issue prescriptions or therapy reports."
          >
            <SignaturePad
              id="stage2-signature"
              value={form.signatureMediaId}
              onChange={(e) => setForm((p) => ({ ...p, signatureMediaId: e.target.value }))}
              title="Your signature"
              mode="both"
              width={400}
              height={180}
            />
          </Section>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              onClick={() => persist(false)}
              disabled={saving || submitting}
            >
              {saving ? 'Saving…' : 'Save progress'}
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                // Phase 14g — if the practitioner hasn't signed yet, require it before submitting.
                // The dedicated dialog draws the canvas centred so the practitioner explicitly
                // attests rather than slipping past an empty signature on a long form.
                if (!form.signatureMediaId) {
                  setSignDialogOpen(true)
                  return
                }
                persist(true)
              }}
              disabled={saving || submitting}
            >
              {submitting ? 'Submitting…' : 'Submit for review'}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Sign-and-submit dialog. Surfaces only when the practitioner clicks Submit without an
          existing signature — keeps Stage 2 friction-free for the rest of the form while still
          enforcing a signature at the moment of attestation. */}
      <Dialog
        open={signDialogOpen}
        onClose={() => !submitting && setSignDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Sign to submit</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Please sign here to confirm the details you&apos;ve entered are accurate. Your signature
            will be used on prescriptions and reports you issue through AUMHUM.
          </DialogContentText>
          <SignaturePad
            id="stage2-signature-dialog"
            value={form.signatureMediaId}
            onChange={(e) => setForm((p) => ({ ...p, signatureMediaId: e.target.value }))}
            title="Your signature"
            mode="draw-only"
            width={420}
            height={180}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSignDialogOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!form.signatureMediaId || submitting}
            onClick={async () => {
              await persist(true)
              setSignDialogOpen(false)
            }}
          >
            {submitting ? 'Submitting…' : 'Sign & submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  )
}

function Section({ title, subtitle, children }) {
  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {subtitle}
        </Typography>
      )}
      {children}
    </Box>
  )
}
